import { useEffect, useState, useRef, useLayoutEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { WhatsAppHeader } from "@/components/whatsapp/WhatsAppWorkspace";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { EmptyState } from "@/components/EmptyState";
import { Pagination } from "@/components/Pagination";
import { Input } from "@/components/Input";
import { Select } from "@/components/Select";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StatusBadge } from "@/components/templates/StatusBadge";
import { FormatBadge } from "@/components/templates/FormatBadge";
import { TemplateGalleryModal } from "@/components/templates/TemplateGalleryModal";
import { TestSendModal } from "@/components/templates/TestSendModal";
import {
  templatesService,
  Template,
  SyncFromMetaResult,
} from "@/services/templates.service";
import { TemplateHoverCard } from "@/components/templates/TemplateHoverCard";
import { TemplatePreview } from "@/components/templates/TemplatePreview";
import { Modal } from "@/components/Modal";
import {
  ListingViewToggle,
  ListingSummary,
  useListingView,
} from "@/components/whatsapp/WhatsAppUi";
import { useDebounced } from "@/hooks/useDebounced";
import { useActiveAccount } from "@/hooks/useActiveAccount";
import {
  Search,
  Plus,
  Sparkles,
  Trash2,
  RefreshCw,
  Send,
  Copy,
  Eye,
  ArrowUpRight,
  FileText,
  Clock3,
  CheckCircle2,
  AlertCircle,
  MessageSquare,
  ImageIcon,
  GalleryHorizontal,
} from "lucide-react";
import clsx from "clsx";
import toast from "react-hot-toast";
import emptyTemplatesIllustration from "@/assets/illustrations/empty-templates.svg";
import { CardGridSkeleton } from '@/components/PageSkeletons';

const STATUS_FILTERS = [
  { value: "", label: "All" },
  { value: "APPROVED", label: "Approved" },
  { value: "PENDING", label: "Pending" },
  { value: "DRAFT", label: "Draft" },
  { value: "REJECTED", label: "Rejected" },
  { value: "PAUSED", label: "Paused" },
  { value: "DISABLED", label: "Disabled" },
];

const CATEGORY_OPTIONS = [
  { value: "", label: "All categories" },
  { value: "marketing", label: "Marketing" },
  { value: "utility", label: "Utility" },
  { value: "authentication", label: "Authentication" },
];

const LANGUAGE_OPTIONS = [
  { value: "", label: "All languages" },
  { value: "en", label: "English" },
  { value: "en_US", label: "English (US)" },
  { value: "en_GB", label: "English (UK)" },
  { value: "ar", label: "Arabic" },
  { value: "es", label: "Spanish" },
  { value: "fr", label: "French" },
  { value: "pt_BR", label: "Portuguese (BR)" },
  { value: "hi", label: "Hindi" },
  { value: "ur", label: "Urdu" },
  { value: "zh_CN", label: "Chinese" },
  { value: "id", label: "Indonesian" },
];

export default function TemplatesList() {
  const [data, setData] = useState<{
    items: Template[];
    total: number;
    page: number;
    limit: number;
  } | null>(null);
  const [hover, setHover] = useState<{
    template: Template;
    anchor: DOMRect;
  } | null>(null);
  const hoverTimer = useRef<number | null>(null);
  const hoverRef = useRef<HTMLDivElement>(null);
  const hideHover = () => {
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    setHover(null);
  };
  const showHover = (template: Template, element: HTMLElement) => {
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    const anchor = element.getBoundingClientRect();
    hoverTimer.current = window.setTimeout(
      () => setHover({ template, anchor }),
      250,
    );
  };
  useEffect(
    () => () => {
      if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
    },
    [],
  );
  useEffect(() => {
    if (!hover) return;
    window.addEventListener("scroll", hideHover, true);
    window.addEventListener("resize", hideHover);
    return () => {
      window.removeEventListener("scroll", hideHover, true);
      window.removeEventListener("resize", hideHover);
    };
  }, [hover]);
  useLayoutEffect(() => {
    if (!hover || !hoverRef.current) return;
    const node = hoverRef.current;
    node.style.left = `${Math.max(12, Math.min(hover.anchor.right + 12, window.innerWidth - 324))}px`;
    node.style.top = `${Math.max(12, Math.min(hover.anchor.top, window.innerHeight - node.offsetHeight - 12))}px`;
  }, [hover]);
  const [view, setView] = useListingView("wa-templates-view");
  const [preview, setPreview] = useState<Template | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const requestId = useRef(0);
  const [q, setQ] = useState("");
  const debouncedQ = useDebounced(q, 300);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");
  const [del, setDel] = useState<Template | null>(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingUid, setSyncingUid] = useState<string | null>(null);
  const [testSendTarget, setTestSendTarget] = useState<Template | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const nav = useNavigate();
  const { active: activeAccount } = useActiveAccount();

  const refresh = async () => {
    const id = ++requestId.current;
    setRefreshing(true);
    setLoadError(false);
    try {
      const result = await templatesService.list({
        q: debouncedQ || undefined,
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
        language: languageFilter || undefined,
        accountUid: activeAccount?.accountUid,
        page,
        limit: 24,
      });
      if (id === requestId.current) setData(result);
    } catch {
      if (id === requestId.current) setLoadError(true);
    } finally {
      if (id === requestId.current) setRefreshing(false);
    }
  };

  const syncFromMeta = async () => {
    setSyncing(true);
    try {
      const result: SyncFromMetaResult = await templatesService.syncFromMeta(activeAccount?.accountUid);
      toast.success(
        `Synced from Meta — ${result.created} created, ${result.updated} updated, ${result.skipped} skipped (${result.total} total)`,
      );
      refresh();
    } catch {
      /* toast shown by interceptor */
    } finally {
      setSyncing(false);
    }
  };

  const syncOne = async (t: Template) => {
    setSyncingUid(t.templateUid);
    try {
      const updated = await templatesService.syncOneFromMeta(t.templateUid);
      const changed = updated.status !== t.status;
      toast.success(
        changed
          ? `${t.name}: ${t.status} → ${updated.status}`
          : `${t.name}: still ${updated.status}`,
      );
      refresh();
    } catch {
      /* toast shown by interceptor */
    } finally {
      setSyncingUid(null);
    }
  };

  useEffect(() => {
    setSelected(new Set());
    refresh();
    // eslint-disable-next-line
  }, [
    debouncedQ,
    page,
    statusFilter,
    categoryFilter,
    languageFilter,
    activeAccount?.accountUid,
  ]);

  const hasFilters =
    !!q || !!statusFilter || !!categoryFilter || !!languageFilter;
  const clearFilters = () => {
    setQ("");
    setStatusFilter("");
    setCategoryFilter("");
    setLanguageFilter("");
    setPage(1);
  };

  const visible = data?.items ?? [];
  const summary = [
    {
      label: "Templates",
      value: data?.total.toLocaleString() ?? "—",
      detail: hasFilters ? "Matching your filters" : "In this account",
      icon: <FileText size={18} />,
    },
    {
      label: "Ready to send",
      value: data ? visible.filter((t) => t.status === "APPROVED").length : "—",
      detail: "Approved on this page",
      icon: <CheckCircle2 size={18} />,
    },
    {
      label: "In review",
      value: data ? visible.filter((t) => t.status === "PENDING").length : "—",
      detail: "Awaiting Meta · this page",
      icon: <Clock3 size={18} />,
    },
    {
      label: "Needs attention",
      value: data
        ? visible.filter((t) =>
            ["REJECTED", "PAUSED", "DISABLED"].includes(t.status),
          ).length
        : "—",
      detail: "Review status · this page",
      icon: <AlertCircle size={18} />,
    },
  ];
  const toggle = (uid: string) =>
    setSelected((current) => {
      const next = new Set(current);
      next.has(uid) ? next.delete(uid) : next.add(uid);
      return next;
    });
  const actions = (t: Template) => (
    <div className="flex items-center gap-1">
      <Button
        size="sm"
        variant="ghost"
        aria-label={`Preview ${t.name}`}
        title="Preview message"
        onClick={() => setPreview(t)}
        className="!px-2"
      >
        <Eye size={15} />
      </Button>
      {t.status !== "DRAFT" && (
        <Button
          size="sm"
          variant="ghost"
          aria-label={`Sync ${t.name}`}
          title="Sync approval status"
          onClick={() => syncOne(t)}
          disabled={syncingUid === t.templateUid}
          className="!px-2"
        >
          <RefreshCw
            size={15}
            className={syncingUid === t.templateUid ? "animate-spin" : ""}
          />
        </Button>
      )}
      {t.status === "APPROVED" && !t.waTemplateId?.startsWith("seed_") && (
        <Button
          size="sm"
          variant="ghost"
          aria-label={`Test ${t.name}`}
          title="Send a test"
          onClick={() => setTestSendTarget(t)}
          className="!px-2"
        >
          <Send size={15} />
        </Button>
      )}
      <Button
        size="sm"
        variant="ghost"
        aria-label={`Duplicate ${t.name}`}
        title="Duplicate template"
        onClick={() => nav(`/templates/new?duplicateFrom=${t.templateUid}`)}
        className="!px-2"
      >
        <Copy size={15} />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        aria-label={`Delete ${t.name}`}
        title="Delete template"
        onClick={() => setDel(t)}
        className="!px-2 hover:!text-danger"
      >
        <Trash2 size={15} />
      </Button>
    </div>
  );
  const message = (t: Template) =>
    (t.body || "Your message content will appear here.")
      .replace(/\\n/g, "\n")
      .replace(
        /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g,
        (all, key: string) =>
          t.previewSamples?.[t.variableMap?.[key] || key] || all,
      );

  return (
    <>
      <WhatsAppHeader
        section="templates"
        title="Templates"
        description="Thoughtful messages. Personal connections. Build your message library."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={syncFromMeta}
              loading={syncing}
              iconLeft={<RefreshCw size={16} />}
            >
              Sync from Meta
            </Button>
            <Button
              onClick={() => nav("/templates/new")}
              iconLeft={<Plus size={16} />}
            >
              Create template
            </Button>
          </div>
        }
      />
      <div className="mx-auto w-full max-w-[1680px] px-4 py-6 sm:px-7 sm:py-8">
        <ListingSummary items={summary} />
        <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-text-primary">
              Your message library
            </h2>
            <p className="mt-1 text-sm text-text-muted">
              {activeAccount?.businessName || "WhatsApp templates"}
              {activeAccount?.displayPhoneNumber
                ? ` · ${activeAccount.displayPhoneNumber}`
                : ""}
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => setGalleryOpen(true)}
            iconLeft={<Sparkles size={15} />}
          >
            Explore template gallery
          </Button>
        </div>
        <div className="mb-6 rounded-2xl border border-border bg-surface px-4 pt-4 sm:px-5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-[180px] flex-1">
              <Input
                aria-label="Search templates"
                iconLeft={<Search size={17} />}
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
                placeholder="Search your templates…"
              />
            </div>
            <Select
              aria-label="Template category"
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value);
                setPage(1);
              }}
              className="!w-auto !h-10"
            >
              {CATEGORY_OPTIONS.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </Select>
            <Select
              aria-label="Template language"
              value={languageFilter}
              onChange={(e) => {
                setLanguageFilter(e.target.value);
                setPage(1);
              }}
              className="!w-auto !h-10"
            >
              {LANGUAGE_OPTIONS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </Select>
            <ListingViewToggle value={view} onChange={setView} />
          </div>
          <div
            className="mt-4 flex items-center gap-5 overflow-x-auto"
            role="group"
            aria-label="Template status"
          >
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => {
                  setStatusFilter(f.value);
                  setPage(1);
                }}
                aria-pressed={statusFilter === f.value}
                className={clsx(
                  "shrink-0 border-b-2 pb-3 text-sm font-medium transition-colors",
                  statusFilter === f.value
                    ? "border-primary text-primary"
                    : "border-transparent text-text-muted hover:text-text-primary",
                )}
              >
                {f.label === "All" ? "All templates" : f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3 text-xs text-text-muted">
          <label className="flex items-center gap-2">
            <input
              aria-label="Select all templates on this page"
              type="checkbox"
              className="h-4 w-4 accent-primary"
              checked={
                !!visible.length &&
                visible.every((t) => selected.has(t.templateUid))
              }
              onChange={(e) =>
                setSelected(
                  e.target.checked
                    ? new Set(visible.map((t) => t.templateUid))
                    : new Set(),
                )
              }
            />
            {selected.size
              ? `${selected.size} selected`
              : `${data?.total ?? 0} templates`}
            {refreshing && <RefreshCw size={12} className="animate-spin" />}
          </label>
          <div className="flex items-center gap-3">
            {hasFilters && (
              <button
                onClick={clearFilters}
                className="font-medium text-primary"
              >
                Clear filters
              </button>
            )}
            {selected.size > 0 && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelected(new Set())}
                >
                  Clear selection
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setBulkConfirmOpen(true)}
                  iconLeft={<Trash2 size={13} />}
                >
                  Delete selected
                </Button>
              </>
            )}
          </div>
        </div>
        {loadError ? (
          <Card className="text-center">
            <p className="mb-3 text-sm text-text-secondary">
              We couldn't load your templates.
            </p>
            <Button variant="secondary" onClick={refresh}>
              Try again
            </Button>
          </Card>
        ) : !data ? (
          <CardGridSkeleton count={6} media className="gap-5 md:grid-cols-2 xl:grid-cols-3" />
        ) : visible.length === 0 ? (
          hasFilters ? (
            <Card className="py-14 text-center">
              <Search className="mx-auto mb-4 text-primary" size={28} />
              <h3 className="font-semibold text-text-primary">
                No matching templates
              </h3>
              <p className="my-2 text-sm text-text-muted">
                Try another name, language or status.
              </p>
              <Button variant="ghost" onClick={clearFilters}>
                Clear filters
              </Button>
            </Card>
          ) : (
            <EmptyTemplates
              onCreate={() => nav("/templates/new")}
              onBrowse={() => setGalleryOpen(true)}
            />
          )
        ) : (
          <>
            {view === "grid" ? (
              <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                {visible.map((t) => (
                  <article
                    key={t.templateUid}
                    className={clsx(
                      "flex min-w-0 flex-col overflow-hidden rounded-2xl border bg-surface transition-shadow hover:shadow-md",
                      selected.has(t.templateUid)
                        ? "border-primary ring-1 ring-primary/20"
                        : "border-border",
                    )}
                  >
                    <div className="flex items-center justify-between px-5 pt-5">
                      <label className="flex items-center gap-2">
                        <input
                          aria-label={`Select ${t.name}`}
                          type="checkbox"
                          className="h-4 w-4 accent-primary"
                          checked={selected.has(t.templateUid)}
                          onChange={() => toggle(t.templateUid)}
                        />
                        <FormatBadge template={t} />
                      </label>
                      <StatusBadge status={t.status} />
                    </div>
                    <Link
                      to={`/templates/${t.templateUid}`}
                      onMouseEnter={(e) => showHover(t, e.currentTarget)}
                      onMouseLeave={hideHover}
                      onFocus={(e) => showHover(t, e.currentTarget)}
                      onBlur={hideHover}
                      className="px-5 pt-4"
                    >
                      <h3
                        title={t.name}
                        className="truncate text-base font-semibold text-text-primary hover:text-primary"
                      >
                        {t.name}
                      </h3>
                      <p className="mt-1 text-xs capitalize text-text-muted">
                        {t.category} <span className="mx-1.5">·</span>{" "}
                        {LANGUAGE_OPTIONS.find((l) => l.value === t.language)
                          ?.label || t.language}
                      </p>
                    </Link>
                    <button
                      type="button"
                      onClick={() => setPreview(t)}
                      aria-label={`Open message preview for ${t.name}`}
                      className="mx-5 my-4 flex h-40 items-start gap-3 overflow-hidden rounded-xl border border-border/60 bg-surface-alt/60 p-4 text-left transition-colors hover:bg-primary-soft/40"
                    >
                      {t.headerImageUrl || t.cards?.[0]?.headerImageUrl ? (
                        <img
                          src={t.headerImageUrl || t.cards?.[0]?.headerImageUrl}
                          alt=""
                          loading="lazy"
                          className="h-20 w-20 shrink-0 rounded-lg object-cover"
                        />
                      ) : (
                        <span className="mt-0.5 text-primary">
                          {t.type === "CAROUSEL" ? (
                            <GalleryHorizontal size={19} />
                          ) : t.headerType === "IMAGE" ? (
                            <ImageIcon size={19} />
                          ) : (
                            <MessageSquare size={19} />
                          )}
                        </span>
                      )}
                      <span className="line-clamp-5 whitespace-pre-wrap break-words text-[13px] leading-relaxed text-text-secondary">
                        {message(t)}
                      </span>
                    </button>
                    <div className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-3">
                      <Link
                        className="inline-flex items-center gap-1 text-xs font-semibold text-text-secondary hover:text-primary"
                        to={`/templates/${t.templateUid}`}
                      >
                        {t.status === "DRAFT"
                          ? "Edit template"
                          : "View template"}
                        <ArrowUpRight size={14} />
                      </Link>
                      {actions(t)}
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-2xl border border-border bg-surface">
                <table className="w-full min-w-[850px] text-left text-sm">
                  <thead className="border-b border-border bg-surface-alt/50 text-xs font-medium text-text-muted">
                    <tr>
                      <th className="w-12 p-4">
                        <span className="sr-only">Selection</span>
                      </th>
                      <th className="py-4 pr-5 font-medium">Template</th>
                      <th className="p-4 font-medium">Format</th>
                      <th className="p-4 font-medium">Status</th>
                      <th className="p-4 font-medium">Last updated</th>
                      <th className="p-4 text-right font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {visible.map((t) => (
                      <tr
                        key={t.templateUid}
                        className={clsx(
                          "transition-colors hover:bg-surface-alt/50",
                          selected.has(t.templateUid) && "bg-primary-soft/40",
                        )}
                      >
                        <td className="p-4">
                          <input
                            aria-label={`Select ${t.name}`}
                            type="checkbox"
                            className="h-4 w-4 accent-primary"
                            checked={selected.has(t.templateUid)}
                            onChange={() => toggle(t.templateUid)}
                          />
                        </td>
                        <td className="max-w-[340px] py-5 pr-5">
                          <Link
                            onMouseEnter={(e) => showHover(t, e.currentTarget)}
                            onMouseLeave={hideHover}
                            onFocus={(e) => showHover(t, e.currentTarget)}
                            onBlur={hideHover}
                            className="block truncate font-semibold text-text-primary hover:text-primary"
                            to={`/templates/${t.templateUid}`}
                          >
                            {t.name}
                          </Link>
                          <div className="mt-1 truncate text-xs capitalize text-text-muted">
                            {t.language} · {t.category} · {message(t)}
                          </div>
                        </td>
                        <td className="p-4">
                          <FormatBadge template={t} />
                        </td>
                        <td className="p-4">
                          <StatusBadge status={t.status} />
                        </td>
                        <td className="whitespace-nowrap p-4 text-xs text-text-muted">
                          {new Date(t.updatedAt).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </td>
                        <td className="p-4">
                          <div className="flex justify-end">{actions(t)}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              page={data.page}
              total={data.total}
              limit={data.limit}
              onChange={setPage}
            />
          </>
        )}
      </div>
      {hover && (
        <div
          role="tooltip"
          ref={hoverRef}
          className="pointer-events-none fixed z-[80] max-h-[calc(100vh-24px)] overflow-hidden rounded-xl"
          style={{ left: 12, top: 12 }}
        >
          <TemplateHoverCard t={hover.template} />
        </div>
      )}
      <Modal
        open={!!preview}
        onClose={() => setPreview(null)}
        title={preview?.name || "Message preview"}
      >
        {preview && (
          <TemplatePreview
            {...preview}
            body={message(preview)}
            buttons={preview.buttons ?? []}
            samples={preview.previewSamples ?? {}}
          />
        )}
      </Modal>
      <ConfirmDialog
        open={!!del}
        title="Delete template?"
        message={`Delete "${del?.name}"? Approved Meta versions will remain on Meta until you delete them there.`}
        danger
        onConfirm={async () => {
          await templatesService.remove(del!.templateUid);
          toast.success("Deleted");
          setDel(null);
          refresh();
        }}
        onCancel={() => setDel(null)}
      />

      {/* Bulk delete */}
      <ConfirmDialog
        open={bulkConfirmOpen}
        title={`Delete ${selected.size} template${selected.size === 1 ? "" : "s"}?`}
        message={`This removes ${selected.size} template${selected.size === 1 ? "" : "s"} from SlightorCRM. Approved Meta versions stay on Meta until you delete them there.`}
        danger
        onConfirm={async () => {
          setBulkDeleting(true);
          try {
            const uids = Array.from(selected);
            const results = await Promise.allSettled(
              uids.map((uid) => templatesService.remove(uid)),
            );
            const failed = results.filter(
              (r) => r.status === "rejected",
            ).length;
            const ok = results.length - failed;
            if (failed === 0)
              toast.success(`Deleted ${ok} template${ok === 1 ? "" : "s"}`);
            else toast.error(`Deleted ${ok}, ${failed} failed`);
            setSelected(new Set());
            setBulkConfirmOpen(false);
            refresh();
          } finally {
            setBulkDeleting(false);
          }
        }}
        onCancel={() => !bulkDeleting && setBulkConfirmOpen(false)}
      />

      <TemplateGalleryModal
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
      />

      <TestSendModal
        open={!!testSendTarget}
        template={testSendTarget}
        onClose={() => setTestSendTarget(null)}
      />
    </>
  );
}

const EmptyTemplates: React.FC<{
  onCreate: () => void;
  onBrowse: () => void;
}> = ({ onCreate, onBrowse }) => (
  <EmptyState
    illustration={emptyTemplatesIllustration}
    title="Your first template starts here."
    description="Templates are the messages you send through WhatsApp campaigns. Build one from scratch, or start from a curated template in the gallery and tweak it."
    action={
      <>
        <Button onClick={onCreate} iconLeft={<Plus size={14} />}>
          New template
        </Button>
        <Button
          variant="secondary"
          onClick={onBrowse}
          iconLeft={<Sparkles size={14} />}
        >
          Browse gallery
        </Button>
      </>
    }
  />
);
